import {
    Calendar,
    addToCalendarsCache,
    getCalendarEntryByEmailAddress,
    getDefaultCalendar,
    removeCalendarFromCalendarsCache,
} from 'owa-calendar-cache';
import type {
    CalendarGroup,
    LinkedCalendarEntry,
    LocalCacheForRemoteCalendarEntry,
} from 'owa-graph-schema';
import { CreateCalendarResult, createCalendarService } from '../services/createCalendarService';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { MailboxInfo, getUserMailboxInfo } from 'owa-client-ids';
import { addCalendarAtIndex, pushCalendar, spliceCalendar } from './modifyCalendarState';
import {
    failedCreateCalendarText,
    successCreateCalendarText,
} from './createLinkedCalendar.locstring.json';
import { getCalendars, setCalendarLoadState } from 'owa-calendar-surface-calendars-store';
import loc, { format } from 'owa-localize';

import CalendarColor from 'owa-service/lib/contract/CalendarColor';
import { ControlIcons } from 'owa-control-icons';
import type { CreateCalendarMutationVariables } from '../graphql/__generated__/createCalendarMutation.interface';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { endAsyncDatapoint } from 'owa-calendar-data-utils/lib/endAsyncDatapoint';
import { findInArray } from 'owa-calendar-data-utils/lib/findInArray';
import { getCalendarColorScheme } from 'owa-calendar-color-resolver';
import { isFeatureEnabled } from 'owa-feature-flags';
import {
    isLocalCacheForRemoteCalendarEntry,
    LINKEDCALENDAR_ENTRY_TYPENAME,
} from 'owa-calendar-properties';
import { lazyShowCalendarNotification } from 'owa-calendar-notification-bar';
import showCalendarErrorNotification from './showCalendarErrorNotification';
import { trace } from 'owa-trace';
import { tryAgainLabel } from 'owa-locstrings/lib/strings/tryagainlabel.locstring.json';

/**
 * Action to create a linked calendar
 * @param {string} newCalendarName - name of new calendar
 * @param {CalendarGroup} group - parent calendar group
 * @param {CreateCalendarState} state - create calendar state with collection of calendars
 */
export let createLinkedCalendar = createLegacyActionWithDatapoint('CreateLinkedCalendarE2E', {
    customData: (
        calendarName: string,
        group: CalendarGroup,
        userIdentity: string,
        emailAddress: string,
        showNotification?: boolean,
        showLoadingIndicatorForLie?: boolean
    ) => {
        return { rejected: !calendarName || calendarName.trim().length == 0 };
    },
})(async function createLinkedCalendar(
    calendarName: string,
    group: CalendarGroup,
    userIdentity: string,
    emailAddress: string,
    showNotification?: boolean,
    showLoadingIndicatorForLie?: boolean
): Promise<Calendar> {
    if (!calendarName || calendarName.trim().length == 0) {
        return null;
    }

    // To check if user added his own calendar
    if (userIdentity.toLowerCase() == emailAddress.toLowerCase()) {
        return getDefaultCalendar();
    }

    const mailboxInfo = getUserMailboxInfo(userIdentity);

    // To check if this user's calendar is already added
    let calEntry = getCalendarEntryByEmailAddress(emailAddress);
    let isDefaultCalendarEntryType = true;

    // In case we received a calendarEntry for the email address, check if it's a primary calendar entry.
    // Users can have a secondary calendar added from another user, and hence this check.
    if (isLocalCacheForRemoteCalendarEntry(calEntry)) {
        let localCalEntry = calEntry as LocalCacheForRemoteCalendarEntry;
        isDefaultCalendarEntryType =
            localCalEntry.IsGeneralScheduleCalendar || localCalEntry.IsDefaultCalendar;
    }

    if (calEntry && isDefaultCalendarEntryType) {
        let calendar = {
            id: {
                id: calEntry.calendarId.id,
                mailboxInfo: mailboxInfo,
            },
            displayName: calEntry.CalendarName,
            calculatedFolderId: emailAddress.toLowerCase(),
            colorScheme: null,
        };

        calendar.colorScheme = getCalendarColorScheme(calendar.id);
        return calendar;
    }

    const dataPoint = new PerformanceDatapoint('CreateLinkedCalendar');
    try {
        let createdCalendar = instantCreateCalendar(calendarName, group, mailboxInfo, emailAddress);

        if (showLoadingIndicatorForLie) {
            setCalendarLoadState(createdCalendar.id, 'Loading');
        }

        let returnCalendar = await executeCreateCalendarRequest(
            createdCalendar,
            dataPoint,
            emailAddress,
            group,
            mailboxInfo,
            calendarName,
            showNotification,
            showLoadingIndicatorForLie
        );

        if (returnCalendar == null) {
            onRequestFailed(mailboxInfo.userIdentity, createdCalendar);

            if (showNotification) {
                showCalendarErrorNotification(
                    'createLinkedCalendar',
                    format(loc(failedCreateCalendarText), calendarName),
                    ControlIcons.Error,
                    loc(tryAgainLabel),
                    () =>
                        createLinkedCalendar(
                            calendarName,
                            group,
                            userIdentity,
                            emailAddress,
                            showNotification,
                            showLoadingIndicatorForLie
                        )
                );
            }
        }

        return returnCalendar;
    } catch (e) {
        // end datapoint with error and re-throw
        dataPoint.endWithError(DatapointStatus.ClientError, e);
        throw e;
    }
});

async function executeCreateCalendarRequest(
    calendar: Calendar,
    dataPoint: PerformanceDatapoint,
    emailAddress: string,
    group: CalendarGroup,
    mailboxInfo: MailboxInfo,
    calendarName: string,
    showNotification: boolean,
    showLoadingIndicatorForLie: boolean
) {
    if (isFeatureEnabled('cal-calendarPickerReorder')) {
        addCalendarAtIndex(getCalendars(), calendar);
    } else {
        pushCalendar(getCalendars(), calendar);
    }

    endAsyncDatapoint(dataPoint); // End scenario as soon as we dispatch the request to create

    if (showNotification) {
        lazyShowCalendarNotification.import().then(showCalendarNotification => {
            showCalendarNotification(
                'createLinkedCalendar',
                'Surface',
                format(loc(successCreateCalendarText), calendarName),
                { icon: ControlIcons.CheckMark }
            );
        });
    }

    try {
        const mutationInput: CreateCalendarMutationVariables = {
            newCalendarName: calendar.displayName,
            parentGroupItemId: group.calendarGroupId.id!,
            parentGroupServerId: group.serverGroupId,
            emailAddress: emailAddress,
            mailboxInfo: mailboxInfo,
            calendarTypename: LINKEDCALENDAR_ENTRY_TYPENAME,
        };

        const mutationResult = await createCalendarService(mutationInput);
        return processServiceResponse(
            mutationResult,
            mailboxInfo,
            calendar,
            getCalendars(),
            group,
            emailAddress,
            showLoadingIndicatorForLie
        );
    } catch (error) {
        return null;
    }
}

function onRequestFailed(userIdentity: string, calendar: Calendar) {
    let calendarIndex = findInArray(getCalendars(), item => {
        return item.id.id == calendar.id.id && item.id.mailboxInfo.userIdentity == userIdentity;
    });

    if (calendarIndex && calendarIndex.index >= 0) {
        spliceCalendar(getCalendars(), calendarIndex.index);
    } else {
        trace.warn(`[renameCalendar] Cannot find calendar id: ${calendar.id}`);
    }
    removeCalendarFromCalendarsCache(calendar);
}

function instantCreateCalendar(
    calendarName: string,
    group: CalendarGroup,
    mailboxInfo: MailboxInfo,
    emailAddress: string
): Calendar {
    let newCalendarId = Math.random().toString();
    let calendar: Calendar = {
        id: {
            id: newCalendarId,
            mailboxInfo: mailboxInfo,
        },
        displayName: calendarName,
        colorScheme: null,
    };

    let calendarEntry: LinkedCalendarEntry = {
        calendarId: {
            id: newCalendarId,
            mailboxInfo: mailboxInfo,
        },
        CalendarName: calendarName,
        OwnerEmailAddress: emailAddress,
        OwnerName: calendarName,
        ParentGroupId: group.serverGroupId,
        DistinguishedFolderId: emailAddress,
        IsGroupMailboxCalendar: false,
    };

    addToCalendarsCache(calendarEntry);
    return calendar;
}

function processServiceResponse(
    mutationResult: CreateCalendarResult,
    mailboxInfo: MailboxInfo,
    calendar: Calendar,
    calendars: Calendar[],
    group: CalendarGroup,
    emailAddress: string,
    showLoadingIndicator: boolean
): Calendar {
    removeCalendarFromCalendarsCache(calendar);

    if (mutationResult) {
        calendar = {
            id: {
                id: mutationResult.calendarId.id,
                changeKey: mutationResult.calendarId.changeKey,
                mailboxInfo: mailboxInfo,
            },
            displayName: calendar.displayName,
            calculatedFolderId: emailAddress,
            colorScheme: null,
        };

        calendars.push(calendar);

        if (showLoadingIndicator) {
            setCalendarLoadState(calendar.id, 'Loading');
        }

        let calendarEntry: LinkedCalendarEntry = {
            calendarId: {
                id: mutationResult.calendarId.id,
                changeKey: mutationResult.calendarId.changeKey,
                mailboxInfo: calendar.id.mailboxInfo,
            },
            CalendarColor: CalendarColor.Auto,
            CalendarName: calendar.displayName,
            OwnerEmailAddress: emailAddress,
            OwnerName: calendar.displayName,
            ParentGroupId: group.serverGroupId,
            IsGeneralScheduleCalendar: true,
            DistinguishedFolderId: emailAddress,
            IsGroupMailboxCalendar: false,
            __typename: 'LinkedCalendarEntry',
        };

        addToCalendarsCache(calendarEntry);
        let colorScheme = getCalendarColorScheme(calendar.id);
        calendar.colorScheme = colorScheme;

        trace.info(
            `[createCalendar] Created a new linked calendar on server: ${calendar.displayName}`
        );
        return calendar;
    } else {
        return null;
    }
}

export default createLinkedCalendar;
